import { isEqual, uniqueId } from 'lodash-es';
import { apiaSelectGetInitialValue } from '../../components/apia/execution/fields/ApiaSelect';
import { getFilePropValue } from '../../components/apia/execution/fields/apiaUploader/util';
import fieldsStore from '../../components/apia/execution/fieldsStore/fieldsStore';
import { parseFileDefinition } from '../../components/common/uploader/UploaderApi';
import { arrayOrArray, noNaN } from '../../utils/util';
import propsStore from '../propsStore/propsStore';
import selectionGetInitialState from '../selection/getInitialState';
import {
  IDataGridContext,
  IDataGridSlice,
  TApiaDataGridFieldRow,
  TApiaDataGridFieldValue,
  TDataCell,
  TDataColumn,
} from './types';
import { makeCellFieldId } from './util';
import ApiaFunctions from '../../JSApi/ApiaFunctions';
import { ISelectField } from '../../JSApi/fields/SelectField';
import { Draft } from '@reduxjs/toolkit';

function getIds(howMuch: number) {
  const returnIds = [];
  for (let i = 0; i < howMuch; i++) returnIds.push(uniqueId());
  return returnIds;
}

function hasModalValue(
  value: TApiaDataGridFieldValue | TApiaDataGridFieldRow,
): value is TApiaDataGridFieldValue {
  return 'qry_show_value' in value;
}

function hasFileValue(
  value: TApiaDataGridFieldValue | TApiaDataGridFieldRow,
): value is TApiaDataGridFieldValue {
  return 'file_prp' in value;
}

function hasPossibleValue(
  value: TApiaDataGridFieldValue | TApiaDataGridFieldRow,
): value is TApiaDataGridFieldRow {
  return 'possibleValue' in value;
}

function getInitialState(context: IDataGridContext): IDataGridSlice {
  return {
    context,
    cells: {},
    editionFormRow: -1,
    isEditionMode: false,
    isGridFocused: false,
    loading: 0,
    showDeleteButtons: {},
    showEditFormButtons: {},
    showDeleteButtonsCount: 0,
    showEditFormButtonsCount: 0,
    showInfoButtons: true,
    updateCellProps: {},
    updating: false,
    ...selectionGetInitialState<TDataColumn, string>(),
    ...(context.grid.field.properties.includeFirstRow
      ? undefined
      : { focusedRow: -1 }),
    allowEdition: true,
    requiredColumns: checkIfRequiredColumns(context),
  };
}

export function checkIfRequiredColumns(context: IDataGridContext) {
  const { field } = context.grid;
  const fields = arrayOrArray(field.field);
  const cols = fields.map((c) => arrayOrArray(c.fieldValue ?? c.row));

  const requiredCells: Record<number, number> = {};

  cols.forEach((rows, colIndex) => {
    rows.forEach((_, rowIndex) => {
      const cellId = makeCellFieldId(context.grid.fieldId, colIndex, rowIndex);
      const propsFromStore = propsStore.getFieldProps(cellId);

      if (propsFromStore?.required) {
        requiredCells[colIndex] = (requiredCells[colIndex] || 0) + 1;
      }
    });
  });

  return Object.entries(requiredCells)
    .map(([key, count]) =>
      count === cols[0].length ? Number.parseInt(key) : -1,
    )
    .filter((c) => c !== -1);
}

export default function processGridField(
  action: string,
  currentState?: Draft<IDataGridSlice<string, TDataColumn>>,
  context?: IDataGridContext,
  props?: { init?: boolean; fromEditionForm?: boolean },
): IDataGridSlice {
  const actualContext = context ?? currentState?.context;
  if (actualContext === undefined)
    throw new Error('There is nor context or state');
  const state = currentState ?? getInitialState(actualContext);
  const { field } = actualContext.grid;

  state.context = actualContext;
  if (!field.properties.gridQuery) {
    const gridHeader = arrayOrArray(field.gridHeader.col);
    const columnsCount = gridHeader.length;

    const fields = arrayOrArray(field.field);
    const rowsCount = arrayOrArray(
      fields[0].fieldValue ?? fields[0].row,
    ).length;

    const shouldUpdateEverything =
      gridHeader.length !== state.columns.length ||
      context?.grid.field.curPage !==
        currentState?.context.grid.field.curPage ||
      state.context.grid.field.curPage !== context?.grid.field.curPage;

    const updatePropsFields: Record<string, TDataCell> = {};

    fields.forEach((currentField, columnIndex) => {
      const fieldValues = currentField.fieldValue
        ? arrayOrArray(currentField.fieldValue)
        : arrayOrArray(currentField.row);

      const { fieldValue, row, ...actualCurrentField } = currentField;

      fieldValues.forEach((currentFieldValue, rowIndex) => {
        if (currentFieldValue.value === '__actionUtils__noUpdate__') return;

        const cellId = makeCellFieldId(
          actualContext.grid.fieldId,
          columnIndex,
          rowIndex,
        );

        let newValue: unknown = hasModalValue(currentFieldValue)
          ? {
              storeValue: currentFieldValue.value,
              showValue: currentFieldValue.qry_show_value,
            }
          : currentFieldValue.value;

        newValue = hasFileValue(currentFieldValue)
          ? Object.values(getFilePropValue(currentFieldValue.file_prp)).map(
              (current) => parseFileDefinition(current, false),
            )
          : newValue;

        if (
          currentField.fieldType === 'select' &&
          hasPossibleValue(currentFieldValue)
        ) {
          newValue = apiaSelectGetInitialValue(currentFieldValue.possibleValue);
          try {
            const form = new ApiaFunctions().getForm(
              context?.form.formData.formName as string,
              context?.form.frmParent,
            );
            if (form) {
              const possibleValueField = form.getField<ISelectField>(
                cellId,
                rowIndex,
              );
              if (possibleValueField) {
                possibleValueField.setOptions(
                  arrayOrArray(currentFieldValue.possibleValue),
                );
              }
            }
          } catch (e) {
            e;
            /** DO NOTHING; THE FIELD DOESN'T EXIST */
          }
        }

        if (currentField.fieldType === 'check') {
          newValue =
            (currentFieldValue.value as unknown) === true ||
            (currentFieldValue.value &&
              ((currentField.valueType === 'S' &&
                currentFieldValue.value === 'true') ||
                (currentField.valueType === 'N' &&
                  String(currentFieldValue.value) === '1'))) ||
            (typeof currentFieldValue.value === 'string' &&
              currentFieldValue.value === '' &&
              !!currentFieldValue.properties?.checked);
        }

        const propsFromStore = propsStore.getFieldProps(cellId);
        const cellData: TDataCell = {
          ...actualCurrentField,
          ...currentFieldValue,
          ...currentFieldValue.properties,
          fieldId: cellId,
          attributes: {
            ...propsFromStore?.attributes,
            ...actualCurrentField.attributes,
            index: rowIndex + noNaN(field.startIndex),
          },
          ...(newValue && !props?.init ? { value: newValue } : undefined),
          message: null,
          name: currentFieldValue.properties?.name
            ? `${currentFieldValue.properties?.name}`
            : `${actualCurrentField.attName}`,
        };

        if (currentFieldValue) {
          const compare = (a: TDataCell, b: TDataCell) => {
            if (!a || !b) return false;

            const { value: noMatter1, ...aProps } = a;
            const { value: noMatter2, ...bProps } = b;

            return isEqual(aProps, bProps);
          };
          if (
            shouldUpdateEverything ||
            undefined === state.cells[cellId] ||
            newValue !== undefined
          ) {
            if (action === 'gotoPage') updatePropsFields[cellId] = cellData;
            else if (!compare(cellData, state.cells[cellId] as TDataCell))
              updatePropsFields[cellId] = cellData;
            if (newValue !== undefined) {
              fieldsStore.getById(cellId)?.setValue(newValue, {
                avoidCleanError: !props?.fromEditionForm,
                setLastValidValue: true,
                setValueOnReadonly: true,
              });
            }
          }
          state.cells[cellId] = cellData;
        }
      });
    });

    Object.entries(updatePropsFields).forEach(([id, props]) => {
      state.updating = true;
      if (context?.grid.field.properties.paged) {
        const field = propsStore.getFieldProps(id)?.field;
        const formData = propsStore.getFieldProps(id)?.formData;
        propsStore.removeField(id);
        propsStore.updateField(
          id,
          { field, formData, ...props },
          { isUrgent: true },
        );
      } else {
        propsStore.updateField(id, props, { isUrgent: true });
      }

      if (props.fieldType === 'file') {
        const currentProps = propsStore.fields[id];
        propsStore.updateField(
          id,
          { ...currentProps, field: props },
          { isUrgent: true },
        );
      }
    });
    state.updating = false;

    if (state.rows.length > rowsCount) {
      Object.keys(state.cells).forEach((current) => {
        const index = Number(current.split('_').pop());

        if (index >= rowsCount) delete state.cells[current];
      });
    }

    if (state.rows.length < rowsCount)
      state.rows = [
        ...state.rows,
        ...getIds(rowsCount - state.rows.length),
      ].splice(0, rowsCount);
    else state.rows = state.rows.slice(0, rowsCount);

    state.columns = shouldUpdateEverything
      ? getIds(columnsCount).map((newColumnId, currentColumnIndex) => {
          return {
            id: newColumnId,
            label: gridHeader[currentColumnIndex].colTitle,
            tooltip: gridHeader[currentColumnIndex].title,
            firingField: fields[currentColumnIndex].id,
            name:
              fields[currentColumnIndex].properties.name ??
              fields[currentColumnIndex].attName ??
              `COLUMN_${currentColumnIndex}`,
            attName:
              fields[currentColumnIndex].attName ||
              `COLUMN_${currentColumnIndex}`,
          };
        })
      : state.columns;
  }

  state.requiredColumns = checkIfRequiredColumns(actualContext);

  return state;
}
